import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import store from "./redux/store";
import axios from "axios";
import App from "./App";
import { createRoot } from "react-dom/client";
import { enqueueSnackbar } from "notistack";
import { ThemeProvider, THEME_STORAGE_KEY } from "./theme/ThemeContext";

// Prevent multiple redirects
let isRedirecting = false;

// -------------------- AXIOS INTERCEPTORS -------------------- //

// Add token before every request
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) config.headers["Authorization"] = "Bearer " + token;
    return config;
  },
  (error) => Promise.reject(error)
);


// Handle token expiry globally
axios.interceptors.response.use(
  (response) => response,

  async (error) => {
    const status = error?.response?.status;
    const currentPath = window.location.pathname;
    const token = localStorage.getItem("token");

    if (status === 401) {

      // do NOT redirect if already on login OR token already missing
      if (!token || currentPath === "/login") {
        return Promise.reject(error);
      }

      if (!isRedirecting) {
        isRedirecting = true;

        // Keep the user's theme choice; everything else is session data
        const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
        localStorage.clear();
        if (savedTheme) localStorage.setItem(THEME_STORAGE_KEY, savedTheme);

        enqueueSnackbar("Session expired. Please login again.", {
          variant: "error",
        });

        setTimeout(() => {
          window.location.replace("/login");
        }, 200);
      }

      return Promise.reject(error);
    }

    // validation errors (callers that show their own error UI pass
    // `skipErrorToast: true` in the request config)
    const errors = error.response?.data?.message?.errors;

    if (errors && !error.config?.skipErrorToast) {
      if (typeof errors === "string" && status === 422) {
        enqueueSnackbar(errors.toString(), { variant: "error" });
      } else {
        Object.values(errors).forEach((msg) => {
          if (msg.toString() !== "Student not found") {
            enqueueSnackbar(msg.toString(), { variant: "error" });
          }
        });
      }
    }

    return Promise.reject(error);
  }
);


// -------------------- RENDER APP -------------------- //

const rootContainer = document.getElementById("root");

if (rootContainer) {
  const root = createRoot(rootContainer);
  root.render(
    <Provider store={store}>
      <BrowserRouter
        future={{ v7_relativeSplatPath: true, v7_startTransition: true }}
      >
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </BrowserRouter>
    </Provider>
  );
} else {
  console.error("Target container is not a valid DOM element.");
}
